import twMerge from 'classnames';
import Router from 'next/router';
import type { ReactChild, ReactNode } from 'react';
import React, {
  cloneElement,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import ReactModal from 'react-modal';

import { frontConfig } from '../../config';
import { useBodyModifier } from '../../hooks/useBodyModifier';
import useElementSize from '../../hooks/useElementSize';
import { ReactComponent as IconArrowPrevious } from '../../icons/icon-arrow-back.svg';
import { NoSSR } from '../../utils/ssr';
import { isBrowser } from '../../utils/web';
import { ButtonV3Icon } from '../Button/ButtonV3Icon';
import { IconCloseV3 } from '../icons/Icons';

ReactModal.setAppElement(frontConfig.rootElementSelector);

const triggerToEventHandler = {
  click: 'onClick',
  mouseenter: 'onMouseEnter',
};

export type ModalInstance = {
  hide: () => void;
  show: () => void;
  state: {
    isVisible: boolean;
  };
};

export type ModalPropTypes = React.PropsWithRef<{
  children?: ReactNode;
  content?: React.ReactChild | React.ReactChild[];
  lazyContent?: (instance: ModalInstance) => React.ReactNode;
  onShow?: () => void;
  onHide?: () => void;
  onCreate?: (instance: ModalInstance) => void;
  trigger?: 'click' | 'mouseenter';
  widthXL?: boolean;
  fullScreen?: boolean;
  /** Show a left arrow on the top left to close the modal, instead of a cross on the top right. */
  closeWithBackButton?: boolean;
  backButtonLabel?: string;
  title?: ReactChild;
  onShowImmediate?: VoidFunction;
  isSubHeaderModal?: boolean;
  shouldFitContentHeight?: boolean;
  contentClassName?: string;
}>;

export const ModalBrowser = memo(
  forwardRef<ModalInstance, ModalPropTypes>((prop, ref) => {
    const {
      children,
      content,
      lazyContent,
      onShow,
      onHide,
      onCreate,
      // showPageOverlay,
      trigger = 'click',
      widthXL,
      fullScreen,
      closeWithBackButton,
      backButtonLabel,
      title,
      onShowImmediate,
      isSubHeaderModal,
      shouldFitContentHeight,
      contentClassName,
    } = prop;
    const [withModal, setShow] = useState(false);
    const showNoHistory = useCallback(() => setShow(true), [setShow]);
    const hideNoHistory = useCallback(() => setShow(false), [setShow]);

    // const { push: show, pop: hide } = useManualHistory(showNoHistory, hideNoHistory);
    const show = useCallback(() => {
      showNoHistory();
      onShowImmediate?.();
    }, [onShowImmediate, showNoHistory]);
    const hide = hideNoHistory;

    const { height: headerHeight } = useElementSize('NavbarHeader');

    const controller = useMemo<ModalInstance>(
      () => ({
        hide,
        show,
        state: {
          isVisible: withModal,
        },
      }),
      [hide, show, withModal],
    );

    useImperativeHandle(ref, () => controller, [controller]);

    const eventHandler = triggerToEventHandler[trigger];

    const childrenToDisplay = useMemo(
      () =>
        eventHandler
          ? cloneElement(children as React.ReactElement, { [eventHandler]: show })
          : children,
      [eventHandler, children, show],
    );

    const bodyModifier = useMemo(
      () => ({
        preventScroll: withModal,
      }),
      [withModal],
    );

    useBodyModifier(bodyModifier);

    // hack on body overflow while using react-modal package
    useEffect(() => {
      if (!withModal) return undefined;

      Router.beforePopState(() => {
        setShow(false);
        return false;
      });

      return () => {
        Router.beforePopState(() => true);
      };
    }, [withModal]);

    useEffect(() => {
      if (!onCreate) return;
      onCreate(controller);
    }, [controller, onCreate]);

    const style = useMemo(
      () =>
        ({
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            padding: fullScreen ? undefined : '5vh max(2%, 10px)',
            zIndex: isSubHeaderModal ? 250 : 800, // should display header popovers above modal if accessibles
            alignItems: 'center',
            ...(isSubHeaderModal && { top: headerHeight }),
          },
          content: {
            position: 'initial',
            padding: '0',
            display: 'block',
            overflow: 'auto',
            flexShrink: 0,
            flexGrow: 0,
            border: 'none',
            backgroundColor: 'transparent',
            width: '100%',
            height: shouldFitContentHeight ? 'auto' : '100%',
            maxHeight: '100%',
            outline: 'none',
          },
        } as const),
      [fullScreen, headerHeight, isSubHeaderModal, shouldFitContentHeight],
    );
    const modalMaxWidhClass = widthXL ? 'max-w-[1090px]' : !fullScreen && 'max-w-[600px]';

    return (
      <NoSSR>
        {childrenToDisplay}
        <ReactModal
          ref={ref as any}
          isOpen={withModal}
          style={style}
          className={twMerge('ReactModal', modalMaxWidhClass, contentClassName)}
          onAfterOpen={onShow}
          onAfterClose={onHide}
          onRequestClose={hide}
          shouldCloseOnOverlayClick
          // open/close styled with packages/front/src/styles/_modal.scss
          // The close animation requires this timeout. It must match the animation timing.
          closeTimeoutMS={100}
        >
          <div
            className={twMerge(
              'Modal bg-white flex flex-col max-h-full',
              fullScreen ? 'w-full h-full' : 'max-w-full',
              !isSubHeaderModal && 'rounded-lg',
            )}
          >
            {closeWithBackButton ? (
              <div
                className="closeWithBackButton flex items-center w-full text-white text-md pt-[17px]"
                tabIndex={-1}
              >
                {backButtonLabel ? (
                  <ButtonV3Icon
                    onClick={hide}
                    icon={
                      <div className="w-5 h-5 flex justify-center items-center">
                        <IconArrowPrevious className="w-[6px] h-[6px] text-primary" />
                      </div>
                    }
                    size="base2"
                    className="withLabel text-gray-soft _lg:pl-[25px]"
                  >
                    {backButtonLabel}
                  </ButtonV3Icon>
                ) : (
                  <ButtonV3Icon
                    onClick={hide}
                    icon={<IconArrowPrevious className="w-8 h-3 text-primary" />}
                  />
                )}
                {title}
              </div>
            ) : (
              <>
                <div className="Modal_header">
                  <div className="close-container p-1 pb-0 flex justify-end">
                    <ButtonV3Icon
                      variant="text"
                      radius={'rounded-full'}
                      size={'sm'}
                      onClick={hide}
                      icon={<IconCloseV3 onClick={hide} className="text-[#BDBDBD]" />}
                    />
                  </div>
                  {title && (
                    <div
                      className="Modal_title px-5 lg:px-8 flex text-md font-semibold"
                      tabIndex={-1}
                    >
                      {title}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="Modal_body flex-1 overflow-y-auto">
              <div className="px-7 pb-7 lg:px-9 lg:pb-9">
                {lazyContent ? lazyContent(controller) : content}
              </div>
            </div>
          </div>
        </ReactModal>
      </NoSSR>
    );
  }),
);

// ReactModal.setAppElement('body');
// eslint-disable-next-line react/no-unused-prop-types
const ModalServer = ({ children }: { children?: ReactNode }) => <>{children}</>;
export const Modal = isBrowser ? ModalBrowser : ModalServer;
